import { useCallback, useEffect, useRef, useState } from 'react'
import type {
  BetResult,
  GameSnapshot,
  Phase,
  PlayerBet,
  Presence,
  SettlementIssue,
  WsEnvelope,
} from './types'

/**
 * Crash client is display-only:
 * - Never invent crash points, odds, or multiplier curves.
 * - Chart time/value come from server fields (elapsed, value).
 * - Client only paints and forwards player intent (place_bet / cashout).
 */

function wsURL(): string {
  const env = (import.meta.env.VITE_WS_URL as string | undefined) ?? ''
  if (env) {
    // Prefer subprotocol JWT (see openCrashSocket); strip legacy ?token= if present.
    const u = new URL(env)
    u.searchParams.delete('token')
    return u.toString()
  }
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
  return `${proto}//${window.location.host}/ws/crash`
}

/** Open Crash WS with JWT via Sec-WebSocket-Protocol (avoids token in URL/logs). */
function openCrashSocket(token: string): WebSocket {
  const url = wsURL()
  try {
    // Server accepts: bearer,<jwt> (see gateway bearerFromWSProtocol).
    return new WebSocket(url, ['bearer', token])
  } catch {
    // Some environments reject custom protocols — fall back to query token.
    const u = new URL(url)
    u.searchParams.set('token', token)
    return new WebSocket(u.toString())
  }
}

function asData(raw: unknown): Record<string, unknown> {
  if (raw == null) return {}
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw) as Record<string, unknown>
    } catch {
      return {}
    }
  }
  if (typeof raw === 'object') return raw as Record<string, unknown>
  return {}
}

function num(v: unknown, fallback = 0): number {
  const n = Number(v)
  return Number.isFinite(n) ? n : fallback
}

function flag(v: unknown): boolean {
  return v === true || v === 'true' || v === 1
}

function sequenceValue(v: unknown): number | null {
  if (v == null || v === '') return null
  const value = Number(v)
  return Number.isSafeInteger(value) && value >= 0 ? value : null
}

function sequenceEpochValue(v: unknown): string | null {
  if (typeof v !== 'string') return null
  const value = v.trim()
  return value.length > 0 ? value : null
}

function roundIdValue(data: Record<string, unknown>): string | null {
  if (data.round_id == null) return null
  const value = String(data.round_id)
  return value.length > 0 ? value : null
}

function parsePresence(data: Record<string, unknown>): Presence {
  return {
    realPlayers: num(data.real_players),
    bots: num(data.bots),
    totalBet: num(data.total_bet),
    watching: num(data.watching),
  }
}

function parseMyBet(raw: unknown): GameSnapshot['myBet'] {
  if (raw == null || typeof raw !== 'object') return null
  const data = raw as Record<string, unknown>
  const autoCashoutAt =
    data.auto_cashout_at != null ? num(data.auto_cashout_at) : undefined
  const cashoutAt = data.cashout_at != null ? num(data.cashout_at) : undefined
  return {
    amount: num(data.amount),
    cashed: flag(data.cashed) || cashoutAt != null,
    autoCashoutAt,
    cashoutAt,
    payout: data.payout != null ? num(data.payout) : undefined,
  }
}

function parseSettlementIssue(raw: unknown): SettlementIssue | null {
  if (raw == null || typeof raw !== 'object') return null
  const data = raw as Record<string, unknown>
  if (data.status !== 'reconciliation_required') return null
  const roundId =
    data.round_id == null || String(data.round_id).length === 0
      ? null
      : String(data.round_id)
  return {
    roundId,
    multiplier: data.multiplier != null ? num(data.multiplier) : undefined,
    payout: data.payout != null ? num(data.payout) : undefined,
    code: String(data.code ?? data.settlement_error ?? 'wallet_outcome_unknown'),
  }
}

function settlementIssueFromResult(b: BetResult): SettlementIssue | null {
  if (b.settlement_status !== 'reconciliation_required') return null
  return {
    roundId: null,
    code: b.settlement_error || 'wallet_outcome_unknown',
  }
}

function playerFromResult(b: BetResult): PlayerBet {
  return {
    userId: b.user_id,
    amount: b.amount,
    cashoutAt: b.cashout_at ?? undefined,
    payout: b.payout,
    status:
      b.settlement_status === 'reconciliation_required'
        ? 'pending'
        : b.cashout_at != null
          ? 'cashed'
          : 'lost',
    isBot: !!b.is_bot,
  }
}

function upsertPlayer(list: PlayerBet[], next: PlayerBet): PlayerBet[] {
  return [...list.filter((p) => p.userId !== next.userId), next]
}

const initial: GameSnapshot = {
  phase: 'waiting',
  roundId: null,
  multiplier: 1,
  crashPoint: null,
  bettingEndsIn: null,
  history: [],
  players: [],
  presence: null,
  myBet: null,
  settlementIssue: null,
  balanceSyncRevision: 0,
  lastError: null,
  connected: false,
}

export function useCrashSocket(token: string | null, userId: number | null) {
  const [snap, setSnap] = useState<GameSnapshot>(initial)
  const wsRef = useRef<WebSocket | null>(null)
  const retryRef = useRef(0)
  const timerRef = useRef<number | null>(null)
  const pingRef = useRef<number | null>(null)
  const generationRef = useRef(0)
  const balanceSyncRevisionRef = useRef(0)
  const pointsRef = useRef<{ t: number; m: number }[]>([])
  const [chartPoints, setChartPoints] = useState<{ t: number; m: number }[]>([])
  const userIdRef = useRef(userId)
  userIdRef.current = userId
  /** Server betting_ends_at (unix ms) for display countdown only. */
  const bettingEndsAtRef = useRef<number | null>(null)

  const clearTimers = () => {
    if (timerRef.current != null) window.clearTimeout(timerRef.current)
    if (pingRef.current != null) window.clearInterval(pingRef.current)
    timerRef.current = null
    pingRef.current = null
  }

  /** Coalesce chart/mult React updates to one paint per frame (perf). */
  const dirtyRef = useRef(false)
  const multRef = useRef(1)
  const flushRafRef = useRef(0)

  const schedulePaint = (generation: number) => {
    if (generationRef.current !== generation) return
    if (dirtyRef.current) return
    dirtyRef.current = true
    const frame = requestAnimationFrame(() => {
      if (
        generationRef.current !== generation ||
        flushRafRef.current !== frame
      ) return
      dirtyRef.current = false
      flushRafRef.current = 0
      setChartPoints(pointsRef.current.slice())
      setSnap((s) => {
        // Never clobber terminal/betting phases with a delayed rAF paint.
        if (s.phase === 'crashed' || s.phase === 'betting') {
          return s
        }
        if (s.phase === 'running' && s.multiplier === multRef.current) {
          return s
        }
        return {
          ...s,
          phase: 'running',
          multiplier: multRef.current,
        }
      })
    })
    flushRafRef.current = frame
  }

  const appendPoint = (elapsed: number, value: number, generation: number) => {
    if (generationRef.current !== generation) return
    const t = Math.max(0, elapsed)
    const m = Math.max(1, value)
    const prev = pointsRef.current
    // Keep time monotonic; replace last sample if server resends same/earlier t.
    let next: { t: number; m: number }[]
    if (prev.length && t <= prev[prev.length - 1].t + 1e-6) {
      next = [...prev.slice(0, -1), { t: Math.max(prev[prev.length - 1].t, t), m }]
    } else {
      next = [...prev, { t, m }]
    }
    pointsRef.current = next.slice(-400)
    multRef.current = m
    schedulePaint(generation)
  }

  const send = useCallback((type: string, data?: unknown) => {
    const ws = wsRef.current
    if (!ws || ws.readyState !== WebSocket.OPEN) return false
    ws.send(JSON.stringify({ type, data, ts: Math.floor(Date.now() / 1000) }))
    return true
  }, [])

  const placeBet = useCallback(
    (amount: number, autoCashoutAt = 0) => {
      setSnap((s) => ({ ...s, lastError: null }))
      const data: { amount: number; auto_cashout_at?: number } = { amount }
      if (autoCashoutAt >= 1.01) data.auto_cashout_at = autoCashoutAt
      return send('place_bet', data)
    },
    [send],
  )

  const cashout = useCallback(() => {
    setSnap((s) => ({ ...s, lastError: null }))
    return send('cashout')
  }, [send])

  useEffect(() => {
    const generation = generationRef.current + 1
    generationRef.current = generation
    if (!token) return
    let stopped = false
    let lastSequence = -1
    let lastSequenceEpoch: string | null = null
    const retiredSequenceEpochs = new Set<string>()
    let currentRoundId: string | null = null
    let hasJoined = false
    let preJoinMessages: WsEnvelope[] = []

    const markBalanceDirty = () => {
      balanceSyncRevisionRef.current += 1
      return balanceSyncRevisionRef.current
    }

    const isCurrentGeneration = () =>
      !stopped && generationRef.current === generation

    const isCurrentSocket = (socket: WebSocket) =>
      isCurrentGeneration() && wsRef.current === socket

    const handleMessage = (msg: WsEnvelope) => {
      if (!isCurrentGeneration()) return
      if (!hasJoined && msg.type !== 'joined') {
        // The server joins the hub before it enqueues the initial snapshot, so
        // public events can arrive first. Replay them only after joined has
        // established the round; otherwise the round guard would discard the
        // sole phase-transition event (for example crash or betting_closed).
        if (preJoinMessages.length >= 256) preJoinMessages.shift()
        preJoinMessages.push(msg)
        return
      }
      const completesHandshake = !hasJoined && msg.type === 'joined'
      if (completesHandshake) hasJoined = true
      const data = asData(msg.data)
      const sequenceEpoch = sequenceEpochValue(data.sequence_epoch)
      if (sequenceEpoch != null) {
        if (lastSequenceEpoch == null) {
          lastSequenceEpoch = sequenceEpoch
          lastSequence = -1
        } else if (sequenceEpoch !== lastSequenceEpoch) {
          // A publisher restart is authoritative at round_start. Never switch
          // back to a retired epoch if an old event arrives late.
          if (
            retiredSequenceEpochs.has(sequenceEpoch) ||
            (msg.type !== 'round_start' && !completesHandshake)
          ) {
            return
          }
          retiredSequenceEpochs.add(lastSequenceEpoch)
          lastSequenceEpoch = sequenceEpoch
          lastSequence = -1
          currentRoundId = null
        }
      }
      const sequence = sequenceValue(data.sequence)
      const staleSequence = sequence != null && sequence < lastSequence
      if (sequence != null) {
        if (staleSequence && msg.type !== 'joined') return
        if (sequence > lastSequence) lastSequence = sequence
      }

      const eventRoundId = roundIdValue(data)
      if (staleSequence && currentRoundId != null) {
        // A broadcast may race ahead of the initial joined snapshot. Confirm
        // the socket and recover same-round private state without allowing the
        // older snapshot to roll public phase/round/chart state backwards.
        const myBet = parseMyBet(data.my_bet)
        const settlementIssue = parseSettlementIssue(data.my_settlement)
        const sameRound =
          eventRoundId != null &&
          (currentRoundId == null || eventRoundId === currentRoundId)
        const balanceSyncRevision =
          (sameRound && myBet != null) || settlementIssue != null
            ? markBalanceDirty()
            : null
        setSnap((s) => ({
          ...s,
          connected: true,
          lastError: null,
          myBet: sameRound && myBet != null ? myBet : s.myBet,
          settlementIssue: settlementIssue ?? s.settlementIssue,
          balanceSyncRevision:
            balanceSyncRevision ?? s.balanceSyncRevision,
        }))
        return
      }

      // If no round has been established yet, the newer event that made this
      // snapshot stale was rejected by the round guard and did not hydrate any
      // public state. Let joined establish the initial round and full snapshot;
      // lastSequence intentionally stays at the newer value.

      const isPrivateSettlementEvent =
        msg.type === 'settlement_error' || msg.type === 'cashout_ok'

      if (msg.type === 'joined') {
        currentRoundId = eventRoundId
      } else if (msg.type === 'round_start') {
        if (eventRoundId == null) return
        currentRoundId = eventRoundId
      } else if (
        eventRoundId != null &&
        eventRoundId !== currentRoundId &&
        !isPrivateSettlementEvent
      ) {
        return
      }

      if (
        msg.type === 'bet_accepted' &&
        (eventRoundId == null || eventRoundId !== currentRoundId)
      ) return

      switch (msg.type) {
        case 'joined': {
          const status = (data.status as Phase) || 'waiting'
          const mult = num(data.multiplier, 1)
          const rid = data.round_id != null ? String(data.round_id) : null
          const myBet = parseMyBet(data.my_bet)
          const settlementIssue = parseSettlementIssue(data.my_settlement)
          const balanceSyncRevision =
            myBet != null || settlementIssue != null ? markBalanceDirty() : null
          if (status === 'running') {
            const elapsed = num(data.elapsed, 0)
            pointsRef.current = [
              { t: 0, m: 1 },
              { t: Math.max(0.01, elapsed), m: mult },
            ]
            setChartPoints([...pointsRef.current])
          } else {
            pointsRef.current = [{ t: 0, m: 1 }]
            setChartPoints([{ t: 0, m: 1 }])
          }
          if (status === 'betting') {
            if (data.betting_ends_at != null) {
              bettingEndsAtRef.current = num(data.betting_ends_at)
            }
            const rem =
              data.betting_remaining != null
                ? num(data.betting_remaining)
                : bettingEndsAtRef.current != null
                  ? Math.max(0, (bettingEndsAtRef.current - Date.now()) / 1000)
                  : null
            setSnap((s) => ({
              ...s,
              connected: true,
              phase: status,
              multiplier: mult,
              roundId: rid ?? s.roundId,
              bettingEndsIn: rem,
              crashPoint: null,
              lastError: null,
              players: markOwnSettlementPending(
                parseJoinedPlayers(data.players, s.players),
                userIdRef.current,
                settlementIssue?.roundId === rid,
              ),
              presence: data.presence
                ? parsePresence(asData(data.presence))
                : s.presence,
              myBet,
              settlementIssue,
              balanceSyncRevision:
                balanceSyncRevision ?? s.balanceSyncRevision,
            }))
          } else {
            bettingEndsAtRef.current = null
            setSnap((s) => ({
              ...s,
              connected: true,
              phase: status,
              multiplier: mult,
              roundId: rid ?? s.roundId,
              bettingEndsIn: null,
              crashPoint:
                status === 'crashed' && data.crash_point != null
                  ? num(data.crash_point)
                  : null,
              lastError: null,
              players: markOwnSettlementPending(
                parseJoinedPlayers(data.players, s.players),
                userIdRef.current,
                settlementIssue?.roundId === rid,
              ),
              presence: data.presence
                ? parsePresence(asData(data.presence))
                : s.presence,
              myBet,
              settlementIssue,
              balanceSyncRevision:
                balanceSyncRevision ?? s.balanceSyncRevision,
            }))
          }
          break
        }
        case 'round_start': {
          // Hard reset board so UI always leaves "crashed" and opens betting.
          pointsRef.current = [{ t: 0, m: 1 }]
          setChartPoints([{ t: 0, m: 1 }])
          const endsAt =
            data.betting_ends_at != null ? num(data.betting_ends_at) : null
          bettingEndsAtRef.current = endsAt
          const rem =
            endsAt != null
              ? Math.max(0, (endsAt - Date.now()) / 1000)
              : num(data.betting_duration, 10)
          setSnap((s) => ({
            ...s,
            connected: true,
            phase: 'betting',
            roundId: String(data.round_id ?? ''),
            multiplier: 1,
            crashPoint: null,
            bettingEndsIn: rem,
            players: [],
            presence: null,
            myBet: null,
            lastError: null,
            // keep s.history crash chips
          }))
          break
        }
        case 'betting_closed':
          bettingEndsAtRef.current = null
          pointsRef.current = [{ t: 0, m: 1 }]
          setChartPoints([{ t: 0, m: 1 }])
          setSnap((s) => ({
            ...s,
            phase: 'running',
            bettingEndsIn: null,
            multiplier: num(data.value, 1),
          }))
          break
        case 'multiplier': {
          // value + elapsed are authoritative from crash engine
          const value = num(data.value, 1)
          const elapsed = num(data.elapsed, 0)
          appendPoint(elapsed, value, generation)
          break
        }
        case 'crash': {
          const cp = num(data.crash_point, num(data.value, 1))
          const elapsed = num(data.elapsed, 0)
          appendPoint(elapsed, cp, generation)
          setSnap((s) => {
            const item = {
              crashPoint: cp,
              roundId: data.round_id != null ? String(data.round_id) : s.roundId ?? undefined,
              serverSeed: data.server_seed != null ? String(data.server_seed) : undefined,
              serverSeedHash:
                data.server_seed_hash != null ? String(data.server_seed_hash) : undefined,
              clientSeed: data.client_seed != null ? String(data.client_seed) : undefined,
              algorithm: data.algorithm != null ? String(data.algorithm) : undefined,
            }
            const history = [item, ...s.history].slice(0, 20)
            const players = s.players.map((p) =>
              p.status === 'active' ? { ...p, status: 'lost' as const } : p,
            )
            let myBet = s.myBet
            if (myBet && !myBet.cashed) {
              myBet = { ...myBet, cashed: false }
            }
            return {
              ...s,
              phase: 'crashed',
              crashPoint: cp,
              multiplier: cp,
              history,
              players,
              myBet,
            }
          })
          break
        }
        case 'round_result': {
          const bets = (data.bets as BetResult[] | undefined) ?? []
          const players: PlayerBet[] = bets.map(playerFromResult)
          const uid = userIdRef.current
          const mine = uid != null ? bets.find((b) => b.user_id === uid) : undefined
          const balanceSyncRevision = mine != null ? markBalanceDirty() : null
          setSnap((s) => {
            let myBet = s.myBet
            let settlementIssue = s.settlementIssue
            if (mine) {
              myBet = {
                amount: mine.amount,
                cashed: mine.cashout_at != null,
                autoCashoutAt: mine.auto_cashout_at ?? undefined,
                cashoutAt: mine.cashout_at ?? undefined,
                payout: mine.payout,
              }
              const resultIssue = settlementIssueFromResult(mine)
              if (resultIssue) {
                settlementIssue = {
                  ...resultIssue,
                  roundId: eventRoundId,
                }
              } else if (
                mine.cashout_at != null &&
                settlementIssue != null &&
                settlementIssue.roundId === eventRoundId
              ) {
                settlementIssue = null
              }
            }
            return {
              ...s,
              players,
              myBet,
              settlementIssue,
              balanceSyncRevision:
                balanceSyncRevision ?? s.balanceSyncRevision,
            }
          })
          break
        }
        case 'bet_accepted': {
          const amount = num(data.amount)
          const autoCashoutAt =
            data.auto_cashout_at != null ? num(data.auto_cashout_at) : undefined
          const uid = userIdRef.current ?? 0
          const balanceSyncRevision = markBalanceDirty()
          setSnap((s) => ({
            ...s,
            myBet: { amount, cashed: false, autoCashoutAt },
            balanceSyncRevision,
            players: upsertPlayer(s.players, {
              userId: uid,
              amount,
              status: 'active',
              isBot: false,
            }),
          }))
          break
        }
        case 'bet_placed': {
          const uid = num(data.user_id)
          const amount = num(data.amount)
          const isBot = flag(data.is_bot)
          if (uid === 0 && !isBot) break
          setSnap((s) => ({
            ...s,
            players: upsertPlayer(s.players, {
              userId: uid,
              amount,
              status: 'active',
              isBot,
            }),
          }))
          break
        }
        case 'player_list': {
          const raw = (data.players as BetResult[] | undefined) ?? []
          if (!raw.length) break
          setSnap((s) => ({
            ...s,
            players: raw.map((b) => {
              const p = playerFromResult(b)
              if (s.phase === 'running' || s.phase === 'betting') {
                return {
                  ...p,
                  status: p.cashoutAt != null ? 'cashed' : 'active',
                }
              }
              return p
            }),
          }))
          break
        }
        case 'presence': {
          setSnap((s) => ({ ...s, presence: parsePresence(data) }))
          break
        }
        case 'cashout':
        case 'cashout_reconciled': {
          const uid = num(data.user_id)
          const mult = num(data.multiplier)
          const payout = num(data.payout)
          const isBot = flag(data.is_bot)
          setSnap((s) => ({
            ...s,
            players: s.players.map((p) =>
              p.userId === uid
                ? {
                    ...p,
                    status: 'cashed' as const,
                    cashoutAt: mult,
                    payout,
                    isBot: p.isBot || isBot,
                  }
                : p,
            ),
          }))
          break
        }
        case 'cashout_ok': {
          const mult = num(data.multiplier)
          const payout = num(data.payout)
          const uid = userIdRef.current
          const balanceSyncRevision = markBalanceDirty()
          setSnap((s) => {
            const appliesToCurrentRound =
              eventRoundId != null && eventRoundId === s.roundId
            const players = s.players.map((p) =>
              appliesToCurrentRound && p.userId === uid
                ? {
                    ...p,
                    status: 'cashed' as const,
                    cashoutAt: mult,
                    payout,
                  }
                : p,
            )
            return {
              ...s,
              myBet: appliesToCurrentRound && s.myBet
                ? { ...s.myBet, cashed: true, cashoutAt: mult, payout }
                : s.myBet,
              players,
              settlementIssue:
                s.settlementIssue != null &&
                (s.settlementIssue.roundId == null ||
                  s.settlementIssue.roundId === eventRoundId)
                  ? null
                  : s.settlementIssue,
              balanceSyncRevision,
              lastError: null,
            }
          })
          break
        }
        case 'settlement_error': {
          const settlementIssue =
            parseSettlementIssue(data) ?? {
              roundId: eventRoundId,
              multiplier:
                data.multiplier != null ? num(data.multiplier) : undefined,
              payout: data.payout != null ? num(data.payout) : undefined,
              code: String(data.code ?? 'wallet_outcome_unknown'),
            }
          const uid = userIdRef.current
          const balanceSyncRevision = markBalanceDirty()
          setSnap((s) => ({
            ...s,
            settlementIssue,
            players: s.players.map((p) =>
              eventRoundId === s.roundId && p.userId === uid
                ? { ...p, status: 'pending' as const }
                : p,
            ),
            balanceSyncRevision,
            lastError: null,
          }))
          break
        }
        case 'error': {
          // Prefer stable code for i18n; fall back to server message.
          const message = String(data.code ?? data.message ?? 'request_failed')
          const isSettlementIssue =
            message === 'settlement_reconciliation_required'
          const balanceSyncRevision = isSettlementIssue
            ? markBalanceDirty()
            : null
          setSnap((s) => ({
            ...s,
            lastError: message,
            settlementIssue: isSettlementIssue
              ? s.settlementIssue ?? {
                  roundId: null,
                  code: message,
                }
              : s.settlementIssue,
            balanceSyncRevision:
              balanceSyncRevision ?? s.balanceSyncRevision,
          }))
          break
        }
        case 'pong':
          break
        default:
          break
      }

      if (completesHandshake && preJoinMessages.length > 0) {
        const queued = preJoinMessages
        preJoinMessages = []
        queued.forEach(handleMessage)
      }
    }

    const connect = () => {
      if (!isCurrentGeneration()) return
      // Reconnects need a fresh joined boundary too. Otherwise a broadcast for
      // a round missed while offline can be round-guarded before the reconnect
      // snapshot and leave the UI on the prior round.
      hasJoined = false
      preJoinMessages = []
      const ws = openCrashSocket(token)
      wsRef.current = ws

      ws.onopen = () => {
        if (!isCurrentSocket(ws)) return
        retryRef.current = 0
        // Stay fail-closed until the authoritative joined snapshot arrives.
        setSnap((s) => ({ ...s, connected: false, lastError: null }))
        pingRef.current = window.setInterval(() => {
          if (!isCurrentSocket(ws)) return
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping' }))
          }
        }, 15000)
      }

      ws.onmessage = (ev) => {
        if (!isCurrentSocket(ws)) return
        let msg: WsEnvelope
        try {
          msg = JSON.parse(String(ev.data)) as WsEnvelope
        } catch {
          return
        }
        handleMessage(msg)
      }

      ws.onerror = () => {
        if (!isCurrentSocket(ws)) return
        /* onclose handles retry */
      }

      ws.onclose = () => {
        if (!isCurrentSocket(ws)) return
        wsRef.current = null
        clearTimers()
        setSnap((s) => ({ ...s, connected: false }))
        const delay = Math.min(800 * 2 ** retryRef.current, 10000)
        retryRef.current += 1
        timerRef.current = window.setTimeout(() => {
          if (!isCurrentGeneration()) return
          connect()
        }, delay)
      }
    }

    setSnap((s) => ({ ...s, connected: false }))
    connect()
    return () => {
      stopped = true
      if (generationRef.current === generation) {
        generationRef.current += 1
      }
      clearTimers()
      if (flushRafRef.current) {
        cancelAnimationFrame(flushRafRef.current)
        flushRafRef.current = 0
      }
      dirtyRef.current = false
      const socket = wsRef.current
      wsRef.current = null
      socket?.close()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token])

  // Display-only countdown from server betting_ends_at (not game authority).
  useEffect(() => {
    if (snap.phase !== 'betting') return
    const id = window.setInterval(() => {
      const ends = bettingEndsAtRef.current
      setSnap((s) => {
        if (s.phase !== 'betting') return s
        if (ends == null) {
          if (s.bettingEndsIn == null) return s
          return { ...s, bettingEndsIn: Math.max(0, s.bettingEndsIn - 0.1) }
        }
        return {
          ...s,
          bettingEndsIn: Math.max(0, (ends - Date.now()) / 1000),
        }
      })
    }, 100)
    return () => window.clearInterval(id)
  }, [snap.phase, snap.roundId])

  return { snap, chartPoints, placeBet, cashout, send }
}

function parseJoinedPlayers(
  raw: unknown,
  fallback: PlayerBet[],
): PlayerBet[] {
  if (!Array.isArray(raw)) return fallback
  return (raw as BetResult[]).map((b) => {
    const p = playerFromResult(b)
    return {
      ...p,
      status:
        p.status === 'pending'
          ? 'pending'
          : p.cashoutAt != null
            ? 'cashed'
            : 'active',
    }
  })
}

function markOwnSettlementPending(
  players: PlayerBet[],
  userId: number | null,
  appliesToRound: boolean,
): PlayerBet[] {
  if (!appliesToRound || userId == null) return players
  return players.map((player) =>
    player.userId === userId
      ? { ...player, status: 'pending' as const }
      : player,
  )
}
